import { useEffect, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Building2, ShieldCheck, TrendingUp, Users, Activity, Award } from "lucide-react";
import { Link } from "react-router-dom";
import { TIER_STYLE, TierBadge, ReputationBadge } from "@/components/network/TierBadge";

export default function NetworkIntelligence() {
  const [intel, setIntel] = useState(null);
  const [pending, setPending] = useState([]);
  const load = async () => {
    try {
      const [i, p] = await Promise.all([
        api.get("/network/intelligence"),
        api.get("/network/directory", { params: { status: "pending" } }),
      ]);
      setIntel(i.data);
      setPending(p.data);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const verify = async (id) => {
    try { await api.post(`/network/organisations/${id}/verify`); toast.success("Verified"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!intel) return (<div><Navbar /><div className="max-w-[1400px] mx-auto px-6 py-10 mono text-sm">Loading network intelligence…</div></div>);

  return (
    <div>
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="kicker">Executive · Trust Network</div>
          <h1 className="display text-4xl">Network Intelligence</h1>
          <p className="text-muted-foreground text-sm mt-1">Ecosystem-wide view of the THOM Verify Trust Network — accreditation, activity, and reputation.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          <StatCard label="Total organisations" value={intel.totals.organisations} Icon={Building2} />
          <StatCard label="Verified" value={intel.totals.verified} Icon={ShieldCheck} accent="text-primary" />
          <StatCard label="Pending review" value={intel.totals.pending} Icon={Users} accent="text-amber-600" />
          <StatCard label="Trusted updates (30d)" value={intel.totals.trusted_updates_30d} Icon={Activity} />
        </div>

        <div className="grid lg:grid-cols-[1fr_1fr] gap-6 mb-8">
          <div className="border border-border p-6 bg-card">
            <div className="kicker">Distribution by tier</div>
            <div className="mt-4 space-y-3">
              {Object.entries(intel.by_tier).map(([t, count]) => {
                const total = Object.values(intel.by_tier).reduce((a,b) => a+b, 0) || 1;
                const pct = Math.round((count / total) * 100);
                return (
                  <div key={t}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <div className="flex items-center gap-2"><TierBadge tier={t} /><span className="text-muted-foreground">{count}</span></div>
                      <div className="mono text-xs text-muted-foreground">{pct}%</div>
                    </div>
                    <div className="h-1.5 bg-secondary rounded-sm overflow-hidden">
                      <div className="h-full" style={{ width: `${pct}%`, background: TIER_STYLE[t]?.ring || "#888" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border border-border p-6 bg-card">
            <div className="kicker">Distribution by category</div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {Object.entries(intel.by_category).sort((a,b) => b[1]-a[1]).map(([c, n]) => (
                <div key={c} className="flex items-center justify-between border-b border-border/60 py-1.5 text-sm">
                  <div className="capitalize">{c.replace(/_/g, " ")}</div>
                  <div className="mono text-xs">{n}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="border border-border bg-card mb-8">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <Award className="h-4 w-4 text-primary" />
            <div className="display text-lg">Top organisations by reputation</div>
          </div>
          <div className="divide-y divide-border">
            {intel.leaderboard.map((o, i) => (
              <Link key={o.id} to={`/network/${o.id}`} className="p-4 flex items-center gap-4 hover:bg-secondary/50" data-testid={`leaderboard-${o.id}`}>
                <div className="w-8 text-center mono text-xs text-muted-foreground">#{i+1}</div>
                <div className="flex-1">
                  <div className="font-semibold">{o.name}</div>
                  <div className="text-xs text-muted-foreground capitalize">{o.category.replace(/_/g, " ")}</div>
                </div>
                <TierBadge tier={o.tier} />
                <ReputationBadge rep={{ band: o.band, score: o.score }} />
                <div className="mono text-xs w-24 text-right">{o.assets_touched} assets</div>
              </Link>
            ))}
            {intel.leaderboard.length === 0 && <div className="p-6 mono text-sm text-muted-foreground">No verified organisations yet.</div>}
          </div>
        </div>

        {/* Pending queue */}
        <div className="border border-border bg-card">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-amber-600" />
            <div className="display text-lg">Pending accreditation ({pending.length})</div>
          </div>
          {pending.length === 0 ? (
            <div className="p-6 mono text-sm text-muted-foreground">No applications waiting.</div>
          ) : pending.map(o => (
            <div key={o.id} className="p-4 border-b border-border last:border-0 flex items-center gap-4" data-testid={`pending-${o.id}`}>
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1">
                <div className="font-semibold">{o.name}</div>
                <div className="text-xs text-muted-foreground capitalize">{o.category.replace(/_/g, " ")} · {o.city}, {o.country}</div>
              </div>
              <TierBadge tier={o.tier} />
              <Button size="sm" onClick={() => verify(o.id)} data-testid={`verify-${o.id}`}>Approve</Button>
              <Link to={`/network/${o.id}`}><Button size="sm" variant="outline">Review</Button></Link>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}

const StatCard = ({ label, value, Icon, accent }) => (
  <div className="border border-border p-4 bg-card">
    <div className="flex items-start justify-between">
      <div>
        <div className="kicker">{label}</div>
        <div className={`display text-3xl mt-1 ${accent || ""}`}>{value}</div>
      </div>
      <Icon className="h-4 w-4 text-primary" />
    </div>
  </div>
);
